import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { usePageMeta } from "@/hooks/usePageMeta";
import SectionHeading from "@/components/base/SectionHeading";
import AnimeCard from "@/components/base/AnimeCard";
import NewsCard from "@/components/base/NewsCard";
import CharacterCard from "@/components/base/CharacterCard";
import SpecialCard from "@/components/base/SpecialCard";
import HeroCarousel from "./components/HeroCarousel";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";
import { characterList } from "@/mocks/characters";
import { specialList } from "@/mocks/specials";

const quickLinks = [
  { label: "动画推荐", icon: "ri-film-line", to: "/category/recommend", tone: "bg-primary-100 text-primary-700" },
  { label: "动漫新闻", icon: "ri-newspaper-line", to: "/category/news", tone: "bg-secondary-100 text-secondary-700" },
  { label: "角色介绍", icon: "ri-user-star-line", to: "/category/characters", tone: "bg-accent-100 text-accent-700" },
  { label: "搜索中心", icon: "ri-search-2-line", to: "/search", tone: "bg-primary-100 text-primary-700" },
];

export default function Home() {
  usePageMeta({
    title: "风车动漫网 - 优质动漫资源与最新动漫资讯平台",
    description:
      "风车动漫网致力于为动漫爱好者提供丰富的动漫资源和最新动漫资讯，涵盖动画推荐、动漫新闻及角色介绍，满足用户全面的动漫需求。",
    keywords: "风车动漫网,动漫资源,动漫资讯,动画推荐,角色介绍,二次元,动漫社区",
  });

  const featuredAnime = [...animeList].sort((a, b) => b.rating - a.rating).slice(0, 6);
  const quickNews = newsList.slice(0, 7);
  const newsGrid = newsList.slice(1, 5);
  const hotAnime = [...animeList].sort((a, b) => b.views - a.views).slice(0, 5);
  const featuredCharacters = characterList.slice(0, 4);

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <HeroCarousel />

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {quickLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="group flex cursor-pointer items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
            >
              <span className={`flex h-11 w-11 flex-none items-center justify-center rounded-md ${link.tone}`}>
                <i className={`${link.icon} text-xl`}></i>
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-bold text-foreground-950 group-hover:text-primary-700">
                  {link.label}
                </span>
                <span className="text-xs text-foreground-500">进入频道</span>
              </span>
            </Link>
          ))}
        </div>
      </div>

      <section className="mx-auto max-w-[1280px] px-4 py-4 md:px-6">
        <SectionHeading
          eyebrow="Anime Recommend"
          title="动画推荐精选"
          description="由编辑团队精选的高口碑动画作品，涵盖科幻、奇幻、治愈等多种题材。"
          moreLink="/category/recommend"
        />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
          {featuredAnime.map((anime) => (
            <AnimeCard key={anime.id} anime={anime} />
          ))}
        </div>
      </section>

      <section className="mt-8 bg-background-100 py-10">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHeading
            eyebrow="Anime News"
            title="动漫新闻"
            description="最新新番情报、业界动态、周边活动与深度访谈，第一时间掌握动漫圈动态。"
            moreLink="/category/news"
          />
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <NewsCard news={newsList[0]} />
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {newsGrid.map((news) => (
                  <NewsCard key={news.id} news={news} />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-foreground-950">
                  <i className="ri-flashlight-line text-accent-500"></i>
                  新闻快讯
                </h3>
                <ul className="flex flex-col divide-y divide-background-100">
                  {quickNews.map((news) => (
                    <li key={news.id}>
                      <Link
                        to={`/detail/${news.id}`}
                        className="group flex cursor-pointer items-start gap-2 py-2.5"
                      >
                        <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-primary-400"></span>
                        <span className="flex-1">
                          <span className="line-clamp-2 text-sm text-foreground-800 transition-colors group-hover:text-primary-700">
                            {news.title}
                          </span>
                          <time className="mt-0.5 block text-xs text-foreground-400">
                            {news.publishTime}
                          </time>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-foreground-950">
                  <i className="ri-fire-line text-accent-500"></i>
                  热门动画榜
                </h3>
                <ul className="flex flex-col gap-2.5">
                  {hotAnime.map((anime, index) => (
                    <li key={anime.id}>
                      <Link
                        to={`/detail/${anime.id}`}
                        className="group flex cursor-pointer items-center gap-3"
                      >
                        <span
                          className={`flex h-6 w-6 flex-none items-center justify-center rounded-md text-xs font-bold ${
                            index < 3
                              ? "bg-accent-500 text-background-50"
                              : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {index + 1}
                        </span>
                        <span className="line-clamp-1 flex-1 text-sm text-foreground-800 transition-colors group-hover:text-primary-700">
                          {anime.title}
                        </span>
                        <span className="text-xs font-medium text-accent-600">
                          {anime.rating.toFixed(1)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-10 md:px-6">
        <SectionHeading
          eyebrow="Characters"
          title="角色介绍热点推荐"
          description="那些让你记住一部作品的角色，看看他们背后的故事与羁绊。"
          moreLink="/category/characters"
        />
        <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
          {featuredCharacters.map((character) => (
            <CharacterCard key={character.id} character={character} />
          ))}
        </div>
      </section>

      <section className="bg-secondary-50 py-10">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHeading
            eyebrow="Specials"
            title="编辑精选专题"
            description="围绕主题策划的内容合集，动画、新闻与角色一次看个够。"
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {specialList.map((special) => (
              <SpecialCard key={special.id} special={special} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6">
        <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:grid-cols-2 md:p-10">
          <div>
            <p className="mb-2 font-label text-xs font-medium uppercase tracking-widest text-accent-600">
              About Windmill Anime
            </p>
            <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">关于风车动漫网</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              风车动漫网是一个面向青少年及年轻动漫爱好者的动漫资源与资讯分享平台。我们聚合最新动画推荐、动漫新闻报道、角色介绍与专题策划，帮助每一位动漫迷快速获取感兴趣的内容。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站所有内容均为虚构示例，用于展示平台的内容组织与浏览体验，不包含登录、支付、下载及在线播放功能。
            </p>
            <Link
              to="/about"
              className="mt-5 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              了解更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { value: `${animeList.length}+`, label: "精选动画", icon: "ri-film-line" },
              { value: `${newsList.length}+`, label: "新闻资讯", icon: "ri-newspaper-line" },
              { value: `${characterList.length}+`, label: "角色资料", icon: "ri-user-star-line" },
              { value: `${specialList.length}`, label: "策划专题", icon: "ri-bookmark-3-line" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-200 bg-background-100 p-4"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                  <i className={`${stat.icon} text-lg`}></i>
                </span>
                <p className="mt-3 text-2xl font-bold text-foreground-950">{stat.value}</p>
                <p className="text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}